"use client";

import { useEffect, useState } from "react";
import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import IconArrowDown from "@/app/(components)/generics/Icons/IconArrowDown";
import IconArrowUp from "@/app/(components)/generics/Icons/IconArrowUp";
import IconPlus from "@/app/(components)/generics/Icons/IconPlus";
import IconTrash from "@/app/(components)/generics/Icons/IconTrash";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { ScriptItemData, ScriptItemInput } from "@/app/types/messages-content/ScriptData";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import { buildScriptPlaceholderRows } from "../../Scripts.logic";
import { matchesFilter, toRows } from "./ScriptItemList.logic";
import styles from "./ScriptItemList.module.css";

type Props = {
  items: ScriptItemData[];
  maxItems: number;
  addLabel: string;
  placeholder: string;
  responsePlaceholder: string;
  isCallMode: boolean;
  isOrdered: boolean;
  isPlaceholder: boolean;
  placeholderRows: number;
  hasResponse: boolean;
  filter: string;
  onSave?: (items: ScriptItemInput[]) => void;
  onSaveResponse?: (label: string, response: string) => void;
};

export default function ScriptItemList(props: Props) {
  const T = useT();
  const {
    items,
    maxItems,
    addLabel,
    placeholder,
    responsePlaceholder,
    isCallMode,
    isOrdered,
    isPlaceholder,
    placeholderRows,
    hasResponse,
    filter,
    onSave,
    onSaveResponse,
  } = props;

  const [rows, setRows] = useState<ScriptItemInput[]>(() => toRows(items));
  const [isAdding, setIsAdding] = useState(false);
  const [openResponseFor, setOpenResponseFor] = useState<string | null>(null);

  useEffect(() => {
    setRows(toRows(items));
  }, [items]);

  const save = (next: ScriptItemInput[]) => {
    setRows(next);
    onSave?.(next);
  };

  const handleUpdate = (index: number, value: string) => {
    const next = [...rows];
    if (value.trim() === "") {
      next.splice(index, 1);
    } else {
      next[index] = { ...next[index], label: value };
    }
    save(next);
  };

  const handleDelete = (index: number) => {
    const next = [...rows];
    next.splice(index, 1);
    save(next);
  };

  const handleMove = (index: number, offset: number) => {
    const target = index + offset;
    if (target < 0 || target >= rows.length) {
      return;
    }
    const next = [...rows];
    [next[index], next[target]] = [next[target], next[index]];
    save(next);
  };

  const handleAdd = (value: string) => {
    setIsAdding(false);
    if (value.trim() === "") {
      return;
    }
    save([...rows, { id: null, label: value }]);
    if (onSaveResponse) {
      setOpenResponseFor(value);
    }
  };

  if (isPlaceholder) {
    return (
      <ul className={UStyles.classNames(styles.list, styles.listPlaceholder)}>
        {buildScriptPlaceholderRows(placeholderRows).map((row, index) => (
          <li key={row.cells[0].width} className={styles.placeholderRow}>
            {isOrdered && <span className={styles.number}>{index + 1}</span>}
            <span className={styles.placeholderBar} style={{ width: row.cells[0].width }} />
          </li>
        ))}
      </ul>
    );
  }

  if (isCallMode) {
    const visible = items
      .filter((item) => !hasResponse || !!item.response)
      .filter((item) => matchesFilter(item, filter));

    if (visible.length === 0) {
      return <p className={styles.filterEmpty}>{T(TKey.MESSAGES_CONTENT_SCRIPT_FILTER_EMPTY)}</p>;
    }

    return (
      <ul className={UStyles.classNames(styles.list, styles.listOpen)}>
        {visible.map((item, index) => (
          <li key={item.id} className={styles.item}>
            <div className={styles.itemHead}>
              {isOrdered && <span className={styles.number}>{index + 1}</span>}
              <span className={item.response ? styles.callCue : styles.callText}>{item.label}</span>
            </div>
            {item.response && <span className={styles.callText}>{item.response}</span>}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className={styles.container}>
      {(rows.length > 0 || isAdding) && (
        <ul className={styles.list}>
          {rows.map((row, index) => {
            const label = row.label;

            return (
              <li
                key={row.id ?? label}
                className={UStyles.classNames(styles.item, styles.itemEditable)}
              >
                <div className={styles.itemHead}>
                  {isOrdered && <span className={styles.number}>{index + 1}</span>}

                  <div className={styles.itemMain}>
                    <EditableText
                      value={label}
                      onSave={(value) => handleUpdate(index, value)}
                      multiline={true}
                      maxLength={Limits.MAX_SCRIPT_ITEM_LABEL}
                      className={styles.editable}
                      textClassName={styles.itemText}
                      inputClassName={styles.itemText}
                      disabled={!onSave}
                    />
                  </div>

                  {onSave && (
                    <div className={styles.itemActions}>
                      <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={() => handleDelete(index)}
                        aria-label={T(TKey.DELETE) as string}
                      >
                        <IconTrash />
                      </button>

                      {isOrdered && (
                        <div className={styles.orderActions}>
                          <button
                            type="button"
                            className={styles.orderBtn}
                            onClick={() => handleMove(index, -1)}
                            disabled={index === 0}
                            aria-label={T(TKey.MESSAGES_CONTENT_SCRIPT_MOVE_UP) as string}
                          >
                            <IconArrowUp />
                          </button>
                          <button
                            type="button"
                            className={styles.orderBtn}
                            onClick={() => handleMove(index, 1)}
                            disabled={index === rows.length - 1}
                            aria-label={T(TKey.MESSAGES_CONTENT_SCRIPT_MOVE_DOWN) as string}
                          >
                            <IconArrowDown />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {onSaveResponse && (
                  <EditableText
                    value={items[index]?.response ?? ""}
                    onSave={(value) => {
                      setOpenResponseFor(null);
                      onSaveResponse(label, value);
                    }}
                    onCancel={() => setOpenResponseFor(null)}
                    placeholder={responsePlaceholder}
                    multiline={true}
                    maxLength={Limits.MAX_SCRIPT_ITEM_RESPONSE}
                    startEditingOnMount={openResponseFor === label}
                    textClassName={styles.responseText}
                    inputClassName={styles.responseText}
                  />
                )}
              </li>
            );
          })}

          {isAdding && (
            <li className={UStyles.classNames(styles.item, styles.itemEditable)}>
              <div className={styles.itemHead}>
                <EditableText
                  value=""
                  placeholder={placeholder}
                  onSave={handleAdd}
                  onCancel={() => setIsAdding(false)}
                  multiline={true}
                  maxLength={Limits.MAX_SCRIPT_ITEM_LABEL}
                  alwaysShowEdit={true}
                  startEditingOnMount={true}
                  className={styles.editable}
                  textClassName={styles.itemText}
                  inputClassName={styles.itemText}
                />
              </div>
            </li>
          )}
        </ul>
      )}

      {onSave && !isAdding && rows.length < maxItems && (
        <button type="button" className={styles.addBtn} onClick={() => setIsAdding(true)}>
          <IconPlus />
          <span>{addLabel}</span>
        </button>
      )}
    </div>
  );
}
